import { v2Api } from './v2Client';

function normalizeSearchPlayer(p) {
  if (!p) return null;
  const fn = p.firstName ?? p.firstname ?? p.first_name ?? p.FirstName ?? '';
  const ln = p.lastName ?? p.lastname ?? p.last_name ?? p.LastName ?? '';
  return {
    id: p.id ?? p.Id,
    first_name: String(fn).trim(),
    last_name: String(ln).trim(),
    picture: p.picture ?? p.Picture ?? '',
  };
}

/** Même algorithme que l’app mobile (expo-crypto SHA-256 hex). */
export async function hashPasswordForApi(plain) {
  const s = typeof plain === 'string' ? plain : '';
  const enc = new TextEncoder().encode(s);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Corps POST /Player/update_player_profile aligné sur `squash_manager_mobile/api/player/player.service.ts`. */
export function buildUpdatePlayerProfileBody(playerId, fields, media = {}) {
  const rawDob = typeof fields.date_of_birth === 'string' ? fields.date_of_birth.trim() : '';
  const dob =
    rawDob.length === 0
      ? null
      : /^\d{4}-\d{2}-\d{2}$/.test(rawDob)
        ? rawDob
        : (() => {
            const d = rawDob.slice(0, 10);
            return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
          })();

  const body = {
    player_id: String(playerId),
    first_name: String(fields.first_name ?? '').trim(),
    last_name: String(fields.last_name ?? '').trim(),
    phone: fields.phone?.trim() ? fields.phone.trim() : null,
    schedule_preference: fields.schedule_preference?.trim() ? fields.schedule_preference.trim() : null,
    date_of_birth: dob,
    address_street: fields.address_street?.trim() ? fields.address_street.trim() : null,
    postal_code: fields.postal_code?.trim() ? fields.postal_code.trim() : null,
    city: fields.city?.trim() ? fields.city.trim() : null,
    nationality: fields.nationality?.trim() ? fields.nationality.trim() : null,
    hand_preference: fields.hand_preference?.trim() ? fields.hand_preference.trim() : null,
    catch_phrase: fields.catch_phrase?.trim() ? fields.catch_phrase.trim() : null,
  };

  if (media.picture !== undefined) {
    const raw = media.picture;
    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) body.picture = '';
    else body.picture = String(raw).trim();
  }
  if (media.banner_image !== undefined) {
    const raw = media.banner_image;
    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) body.banner_image = '';
    else body.banner_image = String(raw).trim();
  }
  if (media.new_password) body.new_password = media.new_password;

  return body;
}

/** POST /Player/admin_update_sport_level — niveau d'un joueur dans un sport (texte court, '' = effacé). */
export async function adminUpdateSportLevel(playerId, sportId, level) {
  const { data } = await v2Api.post('/Player/admin_update_sport_level', {
    player_id: String(playerId),
    sport_id: String(sportId),
    level: level || '',
  });
  return data;
}

/** Profil pour la fiche joueur de l'ERP. `compact_history` : la fiche n'affiche pas l'historique du profil (paginé à
 * part, getPlayerMatchHistoryForErp) — sans lui, la réponse portait tous les matchs du joueur (~700 Ko). */
export async function getPlayerProfileForErp(playerId) {
  const { data } = await v2Api.post('/Player/get_player_profile', { player_id: String(playerId), compact_history: true });
  return data;
}

/** POST /Player/get_player_match_history — kind : box | tournament | other | matches (tous), plus récents d'abord. */
export async function getPlayerMatchHistoryForErp(playerId, { kind = 'matches', limit = 20, offset = 0 } = {}) {
  const { data } = await v2Api.post('/Player/get_player_match_history', {
    player_id: String(playerId),
    kind,
    limit,
    offset,
    sort_key: 'date',
    sort_dir: 'desc',
  });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: Number(data?.total ?? 0) || 0,
    hasMore: Boolean(data?.has_more),
  };
}

/**
 * POST /Player/admin_recent_matches — derniers matchs joués (box + tournoi) d'un joueur, du plus récent au plus ancien,
 * en lignes légères vues de son côté : { match_id, played_at, kind, context, opponents, partners, score_mine,
 * score_theirs, outcome: 'W' | 'L' | null, retired, no_show }.
 */
export async function getPlayerRecentMatchesForErp(playerId, limit = 10) {
  const { data } = await v2Api.post('/Player/admin_recent_matches', { player_id: String(playerId), limit });
  return Array.isArray(data) ? data : [];
}

export async function fetchSchedulePreferencesBulkForErp(playerIds) {
  const ids = Array.isArray(playerIds) ? playerIds.map((x) => String(x)).filter(Boolean) : [];
  const { data } = await v2Api.post('/Player/schedule_preferences_bulk', { player_ids: ids });
  const rows = data?.rows ?? data?.Rows ?? [];
  return Array.isArray(rows) ? rows : [];
}

export async function updatePlayerProfileForErp(body) {
  const { data } = await v2Api.post('/Player/update_player_profile', body);
  return data;
}

/** Ligne de POST /Player/admin_list (AdminPlayerRowDto) normalisée ; null si invalide. */
function normalizeAdminPlayerRow(r) {
  if (!r) return null;
  const id = r.id ?? r.Id;
  if (id == null) return null;
  return {
    id: String(id),
    email: r.email ?? r.Email ?? '',
    first_name: String(r.first_name ?? r.firstName ?? '').trim(),
    last_name: String(r.last_name ?? r.lastName ?? '').trim(),
    active: Boolean(r.active ?? r.Active ?? false),
    role: Number(r.role ?? r.Role ?? 0) || 0,
    picture: r.picture ?? r.Picture ?? '',
    phone: r.phone ?? r.Phone ?? '',
    created_at: r.created_at ?? r.CreatedAt ?? null,
    has_active_box: Boolean(r.has_active_box ?? r.hasActiveBox ?? r.HasActiveBox ?? false),
    has_active_tournament: Boolean(
      r.has_active_tournament ?? r.hasActiveTournament ?? r.HasActiveTournament ?? false,
    ),
    // Champs enrichis de la liste ERP (AdminPlayerRowDto) — voir ErpPlayersPage (colonnes configurables).
    last_active_at: r.last_active_at ?? r.lastActiveAt ?? null,
    last_login_at: r.last_login_at ?? r.lastLoginAt ?? null,
    gender: r.gender ?? null,
    age: r.age ?? null,
    city: r.city ?? null,
    postal_code: r.postal_code ?? null,
    nationality: r.nationality ?? null,
    hand_preference: r.hand_preference ?? null,
    sports: Array.isArray(r.sports) ? r.sports : [],
    current_box_name: r.current_box_name ?? null,
    current_box_level: r.current_box_level ?? null,
    membership_plan: r.membership_plan ?? null,
    membership_until: r.membership_until ?? null,
    wallet_balance_cents: Number(r.wallet_balance_cents ?? 0) || 0,
    matches_played: Number(r.matches_played ?? 0) || 0,
    last_match_at: r.last_match_at ?? null,
    reservations_count: Number(r.reservations_count ?? 0) || 0,
    last_reservation_at: r.last_reservation_at ?? null,
    app_version: r.app_version ?? null,
    login_methods: Array.isArray(r.login_methods) ? r.login_methods : [],
    doinsport_linked: Boolean(r.doinsport_linked),
  };
}

/** POST /Player/admin_list, mode historique (sans offset) : tableau brut, tri par inscription décroissante.
 * `q` multi-mots (prénom, nom, e-mail, téléphone ; casse et accents ignorés). Pour les sélecteurs de joueurs. */
export async function adminListPlayers(opts = {}) {
  const { data } = await v2Api.post('/Player/admin_list', {
    q: opts.q != null ? String(opts.q).trim() : '',
    include_inactive: Boolean(opts.include_inactive),
    limit: Math.min(5000, Math.max(1, Number(opts.limit) || 500)),
    // Sélecteurs : nom, e-mail, photo… suffisent ; pas d'agrégats (box, adhésion, crédit, matchs…).
    include_computed: false,
  });
  const list = Array.isArray(data) ? data : [];
  return list.map(normalizeAdminPlayerRow).filter(Boolean);
}

export const ADMIN_PLAYERS_PAGE_MAX = 200;

function adminPlayersPageBody(opts = {}) {
  return {
    q: opts.q != null ? String(opts.q).trim() : '',
    include_inactive: Boolean(opts.include_inactive),
    offset: Math.max(0, Number(opts.offset) || 0),
    limit: Math.min(ADMIN_PLAYERS_PAGE_MAX, Math.max(1, Number(opts.limit) || 50)),
    filters: Array.isArray(opts.filters) ? opts.filters : [],
    sort: opts.sort || 'created',
    sort_dir: opts.sortDir === 'asc' ? 'asc' : 'desc',
    // Liste ERP : seulement les champs de la fiche (pas d'agrégats box / adhésion / crédit / matchs / réservations /
    // activité, coûteux à chaque page) ; seuls les tris sur ces champs sont acceptés (API : AdminListPlainSortKeys).
    include_computed: false,
    // Compteurs des filtres chargés à part (adminListPlayersCounts) : la page s'affiche sans les attendre.
    include_counts: false,
  };
}

function normalizeAdminPlayerCounts(c) {
  if (!c) return null;
  return {
    all: Number(c.all ?? 0) || 0,
    recent: Number(c.recent ?? 0) || 0,
    box: Number(c.box ?? 0) || 0,
    member: Number(c.member ?? 0) || 0,
    tournament: Number(c.tournament ?? 0) || 0,
    inactive: Number(c.inactive ?? 0) || 0,
  };
}

/** POST /Player/admin_list, mode paginé (offset présent) : page de la liste ERP des joueurs.
 * `filters` : recent | box | member | tournament (ET). `sort` : clé serveur (voir ErpPlayersPage), `sortDir` asc|desc.
 * Sans compteurs (`include_counts: false`) : voir adminListPlayersCounts. */
export async function adminListPlayersPage(opts = {}) {
  const { data } = await v2Api.post('/Player/admin_list', adminPlayersPageBody(opts));
  const items = Array.isArray(data?.items) ? data.items.map(normalizeAdminPlayerRow).filter(Boolean) : [];
  return {
    items,
    total: Number(data?.total ?? 0) || 0,
    hasMore: Boolean(data?.has_more),
  };
}

/** POST /Player/admin_list_counts : compteurs globaux des filtres de la liste ERP (tous, actifs 30 j, en box,
 * adhérents, en tournoi), sans recherche ni filtre ; cache serveur 5 min par club. */
export async function adminListPlayersCounts(opts = {}) {
  const { data } = await v2Api.post('/Player/admin_list_counts', { include_inactive: Boolean(opts.include_inactive) });
  return normalizeAdminPlayerCounts(data) ?? normalizeAdminPlayerCounts({});
}

/** Export : toutes les lignes correspondant à la recherche / aux filtres / au tri, en parcourant admin_list par pages de
 * 200 (mêmes agrégats que l'écran, bornés à chaque page). `onProgress(loaded, total)` facultatif. */
export async function adminListAllPlayers(opts = {}, onProgress) {
  const all = [];
  let offset = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const page = await adminListPlayersPage({ ...opts, offset, limit: ADMIN_PLAYERS_PAGE_MAX });
    all.push(...page.items);
    if (onProgress) onProgress(all.length, page.total);
    if (!page.hasMore || page.items.length === 0) break;
    offset += page.items.length;
  }
  return all;
}

function normalizeMergeStats(raw) {
  if (!raw) return null;
  const id = raw.id ?? raw.Id;
  if (id == null) return null;
  return {
    id: String(id),
    first_name: String(raw.first_name ?? raw.firstName ?? '').trim(),
    last_name: String(raw.last_name ?? raw.lastName ?? '').trim(),
    email: raw.email ?? raw.Email ?? '',
    phone: raw.phone ?? raw.Phone ?? '',
    picture: raw.picture ?? raw.Picture ?? '',
    active: Boolean(raw.active ?? raw.Active ?? false),
    created_at: raw.created_at ?? raw.CreatedAt ?? null,
    last_active_at: raw.last_active_at ?? raw.lastActiveAt ?? raw.LastActiveAt ?? null,
    match_count: Number(raw.match_count ?? raw.matchCount ?? 0) || 0,
    box_membership_count: Number(raw.box_membership_count ?? raw.boxMembershipCount ?? 0) || 0,
    badge_count: Number(raw.badge_count ?? raw.badgeCount ?? 0) || 0,
    message_count: Number(raw.message_count ?? raw.messageCount ?? 0) || 0,
    has_apple_sign_in: Boolean(raw.has_apple_sign_in ?? raw.hasAppleSignIn ?? false),
    has_google_sign_in: Boolean(raw.has_google_sign_in ?? raw.hasGoogleSignIn ?? false),
    has_active_box: Boolean(raw.has_active_box ?? raw.hasActiveBox ?? false),
    has_active_tournament: Boolean(raw.has_active_tournament ?? raw.hasActiveTournament ?? false),
  };
}

export async function adminGetPlayerMergeStats(playerId) {
  const { data } = await v2Api.post('/Player/admin_merge_stats', { player_id: String(playerId) });
  return normalizeMergeStats(data);
}

export async function adminUpdatePlayerMeta(payload) {
  await v2Api.post('/Player/admin_update_meta', {
    player_id: String(payload.player_id),
    email: String(payload.email ?? '').trim(),
    active: Boolean(payload.active),
    role: Number(payload.role) || 0,
  });
}

/** DELETE /Player/admin/{id} — suppression définitive, refusée côté API (409) si le joueur a le
 * moindre historique. Le message d'erreur (à afficher tel quel) invite alors à désactiver le compte. */
export async function adminDeletePlayer(playerId) {
  await v2Api.delete(`/Player/admin/${String(playerId)}`);
}

export async function registerPlayerForErp({ email, passwordPlain, firstName, lastName }) {
  const password = await hashPasswordForApi(passwordPlain);
  const { data } = await v2Api.post('/Player/register', {
    email: String(email).trim(),
    password,
    firstName: String(firstName).trim(),
    lastName: String(lastName).trim(),
  });
  return data;
}

export const playersService = {
  /** POST /Player/search_players — recherche texte (prénom / nom). */
  async searchPlayers(query, limit = 20, includeInactive = false) {
    const q = String(query ?? '').trim();
    if (q.length < 2) return [];
    const { data } = await v2Api.post('/Player/search_players', { q, limit, include_inactive: Boolean(includeInactive) });
    const list = Array.isArray(data) ? data : [];
    return list.map(normalizeSearchPlayer).filter((x) => x && x.id);
  },

  /** GET /Player/admin_phone/{id} — téléphone d'un membre (donnée personnelle, pas exposée par
   * search_players) ; utilisé pour préremplir le téléphone de contact quand un membre est désigné
   * responsable d'une réservation de terrain. */
  async getPlayerPhone(playerId) {
    const { data } = await v2Api.get(`/Player/admin_phone/${String(playerId)}`);
    return data?.phone ?? data?.Phone ?? '';
  },

  /** Joueurs présents dans les matchs de la saison courante (pas de liste joueurs dédiée en V2). */
  async getAll() {
    let seasons;
    try {
      const { data } = await v2Api.get('/Season/current_all');
      seasons = Array.isArray(data) ? data : data ? [data] : [];
    } catch {
      seasons = [];
    }
    if (!seasons || seasons.length === 0) return { data: [] };
    const byId = new Map();
    for (const season of seasons) {
      for (const box of season?.boxes || []) {
        for (const m of box?.matches || []) {
          for (const j of [m?.joueur_a, m?.joueur_b]) {
            if (!j?.id) continue;
            if (!byId.has(j.id)) {
              byId.set(j.id, {
                id: j.id,
                first_name: j.firstname ?? j.first_name,
                last_name: j.lastname ?? j.last_name,
                picture: j.picture,
              });
            }
          }
        }
      }
    }
    return { data: [...byId.values()] };
  },
};

/** Export des joueurs (ERP → Joueurs → Exporter) : colonnes disponibles `{ key, label, group, default }`. */
export async function getPlayerExportColumns() {
  const { data } = await v2Api.get('/Player/admin_export_columns');
  return Array.isArray(data) ? data : [];
}

/** Télécharge le fichier d'export : colonnes (clés, dans l'ordre), statut (active | inactive | all), format (xlsx | csv). */
/** `list` : la liste ERP telle qu'elle est filtrée et triée (mêmes options qu'adminListPlayersPage) ; ses joueurs, dans
 * son ordre, à la place de `status`. */
export async function downloadPlayerExport({ columns, status = 'active', format = 'xlsx', list = null }) {
  let listBody;
  if (list) {
    const { q, include_inactive, filters, sort, sort_dir } = adminPlayersPageBody(list);
    listBody = { q, include_inactive, filters, sort, sort_dir };
  }
  const res = await v2Api.post('/Player/admin_export', { columns, status, format, list: listBody }, { responseType: 'blob' });
  const disposition = res.headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : `joueurs_${new Date().toISOString().slice(0, 10)}.${format}`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
